import React, {
  useLayoutEffect,
  useRef,
  useEffect,
  useCallback,
  useState,
} from 'react';

import './Collapsable.styl';

interface Props {
  style: React.CSSProperties;
  className: string;
  isExpand: boolean;
  lazyLoad: boolean;
  children: React.ReactNode;
}

function Collapsable({
  style,
  className,
  isExpand,
  children,
  lazyLoad,
}: Props) {
  const wrapper = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const initialExpanded = useRef(isExpand);

  const [showChildren, setShowChildren] = useState(
    lazyLoad ? initialExpanded.current : true,
  );

  useLayoutEffect(() => {
    if (!initialExpanded.current && wrapper.current) {
      wrapper.current.style.height = '0';
      wrapper.current.style.opacity = '0';
      wrapper.current.style.overflow = 'hidden';
    }
  }, []);

  const onTransitionEnd = useCallback(() => {
    if (isExpand && wrapper.current) {
      wrapper.current.style.height = '';
      wrapper.current.style.overflow = '';
    }
    if (lazyLoad && !isExpand) {
      setShowChildren(false);
    }
  }, [isExpand, lazyLoad]);

  useEffect(() => {
    if (lazyLoad && isExpand) {
      setShowChildren(true);
    }

    requestAnimationFrame(() => {
      if (!content.current || !wrapper.current) {
        return;
      }

      const { offsetHeight } = content.current;
      if (offsetHeight) {
        const contentHeight = `${offsetHeight}px`;
        wrapper.current.style.height = isExpand ? '0' : contentHeight;
        wrapper.current.style.opacity = isExpand ? '0' : '1';
        wrapper.current.style.overflow = 'hidden';

        requestAnimationFrame(() => {
          if (wrapper.current) {
            wrapper.current.style.height = isExpand ? contentHeight : '0';
            wrapper.current.style.opacity = isExpand ? '1' : '0';
          }
        });
      } else if (isExpand && wrapper.current) {
        wrapper.current.style.height = '';
        wrapper.current.style.overflow = '';
      }
    });
  }, [isExpand, lazyLoad]);

  return (
    <div
      ref={wrapper}
      style={style}
      className={`mio-collapsable ${className}`}
      onTransitionEnd={onTransitionEnd}
    >
      <div ref={content}>{showChildren && children}</div>
    </div>
  );
}

Collapsable.defaultProps = {
  style: {},
  className: '',
  isExpand: false,
} as Partial<Props>;

export default Collapsable;
